import {
  getCareerShiftRunwayData,
  normalizeOnboardingProgressKeys,
  onboardingProgressFromV2OnboardingAiResult,
} from './onboardingUtils';
import { FINANCIAL_CONSTANTS } from './helper';

export function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function normalizeCheckInHistory(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function getCheckedInDateSet(history) {
  return new Set(
    normalizeCheckInHistory(history)
      .map((entry) => entry?.date)
      .filter(Boolean),
  );
}

export function calculateStreak(history) {
  const checkedDates = getCheckedInDateSet(history);
  if (checkedDates.size === 0) return 0;

  const today = getLocalDateString();
  const cursor = new Date();

  // If today isn't checked in yet, allow streak to carry from yesterday
  if (!checkedDates.has(today)) {
    cursor.setDate(cursor.getDate() - 1);
  }

  let streak = 0;
  while (checkedDates.has(getLocalDateString(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function getWeekDateStrings(referenceDate = new Date()) {
  const startOfWeek = new Date(referenceDate);
  const dayOfWeek = startOfWeek.getDay();
  startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);

  const dates = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    dates.push(getLocalDateString(d));
  }
  return dates;
}

export function calculateWeeklyProgress(history, referenceDate = new Date()) {
  const weekDates = getWeekDateStrings(referenceDate);
  const checkedDates = getCheckedInDateSet(history);
  const completedCount = weekDates.filter((date) => checkedDates.has(date)).length;
  return Math.round((completedCount / 7) * 100);
}

export const MICRO_INSIGHT_EMOTIONS = {
  CONFIDENCE: 'confidence',
  CALM: 'calm',
  BALANCE: 'balance',
  STREAK: 'streak',
  DEFAULT: 'default',
};

/** Content reflection confidence and anxiety are whole numbers on this scale. */
export const CHECK_IN_SCORE_MIN = 1;
export const CHECK_IN_SCORE_MAX = 10;
export const DEFAULT_CHECK_IN_CONFIDENCE = 5;
export const DEFAULT_CHECK_IN_ANXIETY = 3;

const HIGH_CONFIDENCE = 7;
const VERY_HIGH_CONFIDENCE = 8;
const LOW_ANXIETY = 3;
const VERY_LOW_ANXIETY = 2;
const HIGH_ANXIETY = 7;
const BALANCED_MIN_CONFIDENCE = 6;
const BALANCED_MAX_ANXIETY = 4;
const SOLID_AVG_CONFIDENCE = 6;
const MANAGEABLE_AVG_ANXIETY = 4;
/** More than half a point on the 1–10 scale counts as a trend. */
const TREND_DELTA = 0.5;

export function clampCheckInScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(CHECK_IN_SCORE_MIN, Math.min(CHECK_IN_SCORE_MAX, Math.round(n)));
}

/** Track fill for a 1–10 range input: 1 sits at the start, 10 fills the track. */
export function checkInScoreTrackPercent(value) {
  const score = clampCheckInScore(value);
  if (score == null) return 0;
  return ((score - CHECK_IN_SCORE_MIN) / (CHECK_IN_SCORE_MAX - CHECK_IN_SCORE_MIN)) * 100;
}

/** Bar width as a share of 10, so a score of 1 stays visible and 10 is full. */
export function checkInScoreBarPercent(value) {
  const score = clampCheckInScore(value);
  if (score == null) return 0;
  return (score / CHECK_IN_SCORE_MAX) * 100;
}

export function formatCheckInScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const clamped = Math.max(CHECK_IN_SCORE_MIN, Math.min(CHECK_IN_SCORE_MAX, n));
  return (Math.round(clamped * 10) / 10).toFixed(1);
}

function getEntriesWithMetrics(history) {
  return normalizeCheckInHistory(history)
    .filter((entry) => entry?.confidence != null && entry?.anxiety != null)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

function countMetricInWeek(entries, weekDates, predicate) {
  const weekSet = new Set(weekDates);
  return entries.filter((entry) => weekSet.has(entry.date) && predicate(entry)).length;
}

function countConsecutive(entries, predicate) {
  let count = 0;
  for (const entry of entries) {
    if (predicate(entry)) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

function pickVariant(variants, seed = 0) {
  return variants[Math.abs(seed) % variants.length];
}

function dayWord(count) {
  return count === 1 ? 'day' : 'days';
}

function buildInsight(message, emotion, seed = 0) {
  return { message, emotion, seed };
}

export function generateMicroInsight(history, referenceDate = new Date()) {
  const entries = getEntriesWithMetrics(history);
  const weekDates = getWeekDateStrings(referenceDate);
  const today = getLocalDateString(referenceDate);
  const todayEntry = entries.find((entry) => entry.date === today);

  const highConfidenceWeek = countMetricInWeek(
    entries,
    weekDates,
    (entry) => entry.confidence >= HIGH_CONFIDENCE,
  );
  if (highConfidenceWeek >= 3) {
    return buildInsight(
      pickVariant(
        [
          `That's ${highConfidenceWeek} ${dayWord(highConfidenceWeek)} of high confidence this week. Keep it up!`,
          `You've logged ${highConfidenceWeek} confident ${dayWord(highConfidenceWeek)} this week — momentum is building!`,
          `${highConfidenceWeek} high-confidence reflections this week. You're showing up for yourself.`,
        ],
        highConfidenceWeek,
      ),
      MICRO_INSIGHT_EMOTIONS.CONFIDENCE,
      highConfidenceWeek,
    );
  }

  const calmWeek = countMetricInWeek(
    entries,
    weekDates,
    (entry) => entry.anxiety <= LOW_ANXIETY,
  );
  if (calmWeek >= 3) {
    return buildInsight(
      pickVariant(
        [
          `That's ${calmWeek} calm ${dayWord(calmWeek)} this week. Your mind is settling in.`,
          `${calmWeek} low-anxiety reflections this week — you're creating space to think clearly.`,
          `Three-plus calm days this week. Financial peace starts with how you feel.`,
        ],
        calmWeek,
      ),
      MICRO_INSIGHT_EMOTIONS.CALM,
      calmWeek,
    );
  }

  const highConfidenceStreak = countConsecutive(
    entries,
    (entry) => entry.confidence >= HIGH_CONFIDENCE,
  );
  if (highConfidenceStreak >= 2) {
    return buildInsight(
      pickVariant(
        [
          `${highConfidenceStreak} ${dayWord(highConfidenceStreak)} in a row feeling confident. You're on a roll!`,
          `${highConfidenceStreak} consecutive high-confidence days — trust that feeling.`,
          `High confidence ${highConfidenceStreak} ${dayWord(highConfidenceStreak)} running. Keep riding it.`,
        ],
        highConfidenceStreak,
      ),
      MICRO_INSIGHT_EMOTIONS.CONFIDENCE,
      highConfidenceStreak,
    );
  }

  const calmStreak = countConsecutive(entries, (entry) => entry.anxiety <= LOW_ANXIETY);
  if (calmStreak >= 2) {
    return buildInsight(
      pickVariant(
        [
          `${calmStreak} ${dayWord(calmStreak)} of staying calm. That's real progress.`,
          `${calmStreak} calm reflections in a row — you're easing the noise around money.`,
          `Low anxiety ${calmStreak} ${dayWord(calmStreak)} straight. Breathe into that.`,
        ],
        calmStreak,
      ),
      MICRO_INSIGHT_EMOTIONS.CALM,
      calmStreak,
    );
  }

  if (entries.length >= 4) {
    const recent = entries.slice(0, 3);
    const previous = entries.slice(3, 6);
    if (previous.length >= 3) {
      const recentConf = recent.reduce((sum, e) => sum + e.confidence, 0) / recent.length;
      const prevConf = previous.reduce((sum, e) => sum + e.confidence, 0) / previous.length;
      if (recentConf - prevConf > TREND_DELTA) {
        return buildInsight(
          pickVariant(
            [
              'Your confidence is trending up — keep it going!',
              'Confidence is climbing compared to last week. Stay with that energy.',
              "You're feeling more sure of yourself lately. That matters.",
            ],
            Math.round(recentConf),
          ),
          MICRO_INSIGHT_EMOTIONS.CONFIDENCE,
          Math.round(recentConf),
        );
      }

      const recentAnx = recent.reduce((sum, e) => sum + e.anxiety, 0) / recent.length;
      const prevAnx = previous.reduce((sum, e) => sum + e.anxiety, 0) / previous.length;
      if (prevAnx - recentAnx > TREND_DELTA) {
        return buildInsight(
          pickVariant(
            [
              "Your anxiety is easing — that's a win worth noticing.",
              'Money stress has been trending down. Keep nurturing that calm.',
              "You're feeling less anxious than before. Small shifts add up.",
            ],
            Math.round(prevAnx - recentAnx),
          ),
          MICRO_INSIGHT_EMOTIONS.CALM,
          Math.round(prevAnx - recentAnx),
        );
      }
    }
  }

  if (todayEntry?.confidence >= VERY_HIGH_CONFIDENCE) {
    return buildInsight(
      pickVariant(
        [
          "You're feeling especially confident today — own it!",
          'High confidence today. Let that guide your next money move.',
          'Today reads confident. Trust what you already know.',
        ],
        todayEntry.confidence,
      ),
      MICRO_INSIGHT_EMOTIONS.CONFIDENCE,
      todayEntry.confidence,
    );
  }

  if (todayEntry?.anxiety <= VERY_LOW_ANXIETY) {
    return buildInsight(
      pickVariant(
        [
          "A calm reflection today — you're in a good headspace.",
          "Low anxiety today. That's the foundation for clear decisions.",
          'You feel grounded today. Hold onto that feeling.',
        ],
        todayEntry.anxiety,
      ),
      MICRO_INSIGHT_EMOTIONS.CALM,
      todayEntry.anxiety,
    );
  }

  if (
    todayEntry
    && todayEntry.confidence >= BALANCED_MIN_CONFIDENCE
    && todayEntry.anxiety <= BALANCED_MAX_ANXIETY
  ) {
    return buildInsight(
      pickVariant(
        [
          'Balanced emotions today — confidence and calm are both showing up.',
          "Steady confidence with manageable anxiety today. That's a sweet spot.",
          'You feel centered today — neither rushed nor overwhelmed.',
        ],
        todayEntry.confidence + todayEntry.anxiety,
      ),
      MICRO_INSIGHT_EMOTIONS.BALANCE,
      todayEntry.confidence,
    );
  }

  if (todayEntry?.anxiety >= HIGH_ANXIETY) {
    return buildInsight(
      pickVariant(
        [
          "Tough day on the anxiety meter — showing up still counts.",
          "High anxiety today is valid. One reflection won't fix everything, but it helps you see it.",
          "Money stress feels loud today. You're not alone in that.",
        ],
        todayEntry.anxiety,
      ),
      MICRO_INSIGHT_EMOTIONS.CALM,
      todayEntry.anxiety,
    );
  }

  const streak = calculateStreak(history);
  if (streak >= 3) {
    return buildInsight(
      pickVariant(
        [
          `${streak}-day reflection streak! Consistency builds wealth.`,
          `${streak} ${dayWord(streak)} showing up — richuels are forming.`,
          `Streak: ${streak} ${dayWord(streak)}. Keep the rhythm going.`,
        ],
        streak,
      ),
      MICRO_INSIGHT_EMOTIONS.STREAK,
      streak,
    );
  }

  const recent = entries.slice(0, 7);
  if (recent.length >= 3) {
    const avgConfidence = recent.reduce((sum, entry) => sum + entry.confidence, 0) / recent.length;
    if (avgConfidence >= SOLID_AVG_CONFIDENCE) {
      return buildInsight(
        pickVariant(
          [
            'Solid confidence across your recent reflections — stay curious, not critical.',
            "Your recent mood skews confident. Build on what's working.",
            "Confidence has been steady lately. That's worth celebrating.",
          ],
          Math.round(avgConfidence),
        ),
        MICRO_INSIGHT_EMOTIONS.CONFIDENCE,
        Math.round(avgConfidence),
      );
    }

    const avgAnxiety = recent.reduce((sum, entry) => sum + entry.anxiety, 0) / recent.length;
    if (avgAnxiety <= MANAGEABLE_AVG_ANXIETY) {
      return buildInsight(
        pickVariant(
          [
            'Your recent reflections show manageable anxiety — nice baseline.',
            "You've been keeping money stress in check. Keep that practice alive.",
            'Calm has been your recent theme. Protect that headspace.',
          ],
          Math.round(avgAnxiety),
        ),
        MICRO_INSIGHT_EMOTIONS.CALM,
        Math.round(avgAnxiety),
      );
    }
  }

  return buildInsight(
    pickVariant(
      [
        'Every small step counts toward your financial wellness.',
        'Noticing how you feel after a Quick Richuel is already a win — keep going.',
        "Awareness is the first richuel. You're building it one day at a time.",
      ],
      entries.length,
    ),
    MICRO_INSIGHT_EMOTIONS.DEFAULT,
    entries.length,
  );
}

export function generateProgressSummary(history) {
  const entries = normalizeCheckInHistory(history);

  // Sort once; reuse the sorted array for last3, trend, and averages
  const withMetrics = entries
    .filter((entry) => entry?.confidence != null)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const avgConfidence = withMetrics.length
    ? Math.round(withMetrics.reduce((sum, e) => sum + e.confidence, 0) / withMetrics.length)
    : null;
  const avgAnxiety = withMetrics.length
    ? Math.round(withMetrics.reduce((sum, e) => sum + e.anxiety, 0) / withMetrics.length)
    : null;

  const last3 = withMetrics.slice(0, 3);

  let confidenceTrend = 'neutral';
  if (withMetrics.length >= 4) {
    const recent = withMetrics.slice(0, 3);
    const previous = withMetrics.slice(3, 6);
    const recentAvg = recent.reduce((s, e) => s + e.confidence, 0) / recent.length;
    const prevAvg = previous.reduce((s, e) => s + e.confidence, 0) / previous.length;
    if (recentAvg - prevAvg > TREND_DELTA) confidenceTrend = 'up';
    else if (prevAvg - recentAvg > TREND_DELTA) confidenceTrend = 'down';
  }

  return {
    streak: calculateStreak(history),
    weeklyPct: calculateWeeklyProgress(history),
    totalCheckIns: entries.length,
    avgConfidence,
    avgAnxiety,
    confidenceTrend,
    last3,
    allCheckIns: withMetrics,
  };
}

export function upsertContentReflection(history, entry) {
  const normalized = normalizeCheckInHistory(history);
  const contentId = String(entry?.content_id || '');
  const next = { ...entry };
  if (next.confidence != null) next.confidence = clampCheckInScore(next.confidence);
  if (next.anxiety != null) next.anxiety = clampCheckInScore(next.anxiety);
  return [
    ...normalized.filter((item) => String(item?.content_id || '') !== contentId || !contentId),
    next,
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}

const WEEK_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function getWeekDateStringsMonFirst(referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  const day = ref.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(ref);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(ref.getDate() + diffToMonday);

  const dates = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(getLocalDateString(d));
  }
  return dates;
}

export function formatWeekRangeLabel(weekDates) {
  if (!weekDates?.length) return '';
  const start = new Date(`${weekDates[0]}T12:00:00`);
  const end = new Date(`${weekDates[weekDates.length - 1]}T12:00:00`);
  const month = start.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${start.getDate()} - ${end.getDate()}`;
}

/**
 * Charts already plot confidence and anxiety from 1 to 10.
 * Stored scores use that same scale, so this only clamps them onto it.
 */
export function scaleMetricToChart(value) {
  return clampCheckInScore(value);
}

function averageReflectionsByDate(history) {
  const groups = new Map();
  normalizeCheckInHistory(history).forEach((entry) => {
    if (!entry?.date) return;
    const bucket = groups.get(entry.date) || [];
    bucket.push(entry);
    groups.set(entry.date, bucket);
  });

  const averageOf = (values) => (
    values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null
  );

  const byDate = {};
  groups.forEach((entries, date) => {
    const confidenceValues = entries
      .map((entry) => Number(entry.confidence))
      .filter((value) => Number.isFinite(value));
    const anxietyValues = entries
      .map((entry) => Number(entry.anxiety))
      .filter((value) => Number.isFinite(value));
    byDate[date] = {
      date,
      confidence: averageOf(confidenceValues),
      anxiety: averageOf(anxietyValues),
      completed: true,
    };
  });
  return byDate;
}

function countReflectionsOnDates(history, dates) {
  const dateSet = dates instanceof Set ? dates : new Set(dates);
  return normalizeCheckInHistory(history).filter((entry) => (
    entry?.date
    && dateSet.has(entry.date)
    && (entry.confidence != null || entry.anxiety != null)
  )).length;
}

function getRichuelCompletedDateSet(history) {
  const dates = new Set();
  normalizeCheckInHistory(history).forEach((entry) => {
    if (!entry?.date) return;
    if (entry.completed || (entry.confidence != null && entry.anxiety != null)) {
      dates.add(entry.date);
    }
  });
  return dates;
}

function interpolateMetricValues(values) {
  const result = [...values];
  const knownIndices = result
    .map((v, i) => (v != null ? i : -1))
    .filter((i) => i >= 0);

  if (knownIndices.length === 0) return result.map(() => 5);
  if (knownIndices.length === 1) {
    const only = result[knownIndices[0]];
    return result.map((v) => (v != null ? v : only));
  }

  const first = knownIndices[0];
  const last = knownIndices[knownIndices.length - 1];
  for (let i = 0; i < first; i += 1) result[i] = result[first];
  for (let i = last + 1; i < result.length; i += 1) result[i] = result[last];

  for (let k = 0; k < knownIndices.length - 1; k += 1) {
    const left = knownIndices[k];
    const right = knownIndices[k + 1];
    const leftVal = result[left];
    const rightVal = result[right];
    const span = right - left;
    for (let i = left + 1; i < right; i += 1) {
      const t = (i - left) / span;
      result[i] = Math.round(leftVal + (rightVal - leftVal) * t);
    }
  }
  return result;
}

function computeAnxietyDropInsight(history, richuelDates, richuelThreshold = 3) {
  const entries = normalizeCheckInHistory(history)
    .filter((e) => e?.anxiety != null)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (entries.length < 2) {
    return { richuelThreshold, anxietyDropPct: 0, hasRealData: false };
  }

  const richuelAnxieties = entries
    .filter((e) => richuelDates.has(e.date))
    .map((e) => e.anxiety);
  const otherAnxieties = entries
    .filter((e) => !richuelDates.has(e.date))
    .map((e) => e.anxiety);

  if (richuelAnxieties.length >= richuelThreshold && otherAnxieties.length > 0) {
    const avgRichuel = richuelAnxieties.reduce((s, v) => s + v, 0) / richuelAnxieties.length;
    const avgOther = otherAnxieties.reduce((s, v) => s + v, 0) / otherAnxieties.length;
    if (avgOther > 0 && avgRichuel < avgOther) {
      const drop = Math.round(((avgOther - avgRichuel) / avgOther) * 100);
      return {
        richuelThreshold,
        anxietyDropPct: Math.max(5, Math.min(60, drop)),
        hasRealData: true,
        comparisonType: 'richuel-vs-non-richuel',
      };
    }
  }

  const weekDates = getWeekDateStringsMonFirst();
  const weekSet = new Set(weekDates);
  const weekRichuelCount = weekDates.filter((d) => richuelDates.has(d)).length;
  if (weekRichuelCount >= richuelThreshold) {
    const weekEntries = entries.filter((e) => weekSet.has(e.date));
    const early = weekEntries.slice(0, Math.ceil(weekEntries.length / 2));
    const late = weekEntries.slice(Math.ceil(weekEntries.length / 2));
    if (early.length && late.length) {
      const earlyAvg = early.reduce((s, e) => s + e.anxiety, 0) / early.length;
      const lateAvg = late.reduce((s, e) => s + e.anxiety, 0) / late.length;
      if (earlyAvg > lateAvg) {
        const drop = Math.round(((earlyAvg - lateAvg) / earlyAvg) * 100);
        return {
          richuelThreshold,
          anxietyDropPct: Math.max(10, Math.min(50, drop)),
          hasRealData: true,
          comparisonType: 'weekly-trend',
        };
      }
    }
  }

  return { richuelThreshold, anxietyDropPct: 0, hasRealData: false };
}

function computeConfidenceRiseInsight(history, richuelDates, richuelThreshold = 2) {
  const entries = normalizeCheckInHistory(history)
    .filter((e) => e?.confidence != null)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (entries.length < 2) {
    return { confidenceRisePct: 0, hasRealData: false };
  }

  const richuelConfidence = entries
    .filter((e) => richuelDates.has(e.date))
    .map((e) => e.confidence);
  const otherConfidence = entries
    .filter((e) => !richuelDates.has(e.date))
    .map((e) => e.confidence);

  if (richuelConfidence.length >= richuelThreshold && otherConfidence.length > 0) {
    const avgRichuel = richuelConfidence.reduce((s, v) => s + v, 0) / richuelConfidence.length;
    const avgOther = otherConfidence.reduce((s, v) => s + v, 0) / otherConfidence.length;
    if (avgOther > 0 && avgRichuel > avgOther) {
      const rise = Math.round(((avgRichuel - avgOther) / avgOther) * 100);
      return {
        confidenceRisePct: Math.max(5, Math.min(60, rise)),
        hasRealData: true,
      };
    }
  }

  const weekDates = getWeekDateStringsMonFirst();
  const weekSet = new Set(weekDates);
  const weekEntries = entries.filter((e) => weekSet.has(e.date));
  if (weekEntries.length >= 2) {
    const early = weekEntries.slice(0, Math.ceil(weekEntries.length / 2));
    const late = weekEntries.slice(Math.ceil(weekEntries.length / 2));
    if (early.length && late.length) {
      const earlyAvg = early.reduce((s, e) => s + e.confidence, 0) / early.length;
      const lateAvg = late.reduce((s, e) => s + e.confidence, 0) / late.length;
      if (earlyAvg > 0 && lateAvg > earlyAvg) {
        const rise = Math.round(((lateAvg - earlyAvg) / earlyAvg) * 100);
        return {
          confidenceRisePct: Math.max(5, Math.min(50, rise)),
          hasRealData: true,
        };
      }
    }
  }

  return { confidenceRisePct: 0, hasRealData: false };
}

function formatRunwayIncreaseLabel(months) {
  if (months == null || !Number.isFinite(months) || months <= 0) return null;
  const rounded = Math.round(months * 10) / 10;
  const display = rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
  return `+${display} ${rounded === 1 ? 'month' : 'months'}`;
}

function resolveQuickRichuelFinancialImpact(v2OnboardingAiResult, completedCount, totalCount) {
  const progress = onboardingProgressFromV2OnboardingAiResult(v2OnboardingAiResult)
    || v2OnboardingAiResult?.onboardingProgress
    || null;
  if (!progress) {
    return { hasRunwayFromProfile: false, runwayIncreaseLabel: null, longevityIncreaseLabel: null };
  }

  const runway = getCareerShiftRunwayData(normalizeOnboardingProgressKeys(progress));
  const completionRatio = totalCount > 0 ? completedCount / totalCount : 1;
  const longevityIncreasePct = Math.min(15, Math.max(2, Math.round(completedCount * 2 * completionRatio)));
  const longevityIncreaseLabel = `+${longevityIncreasePct}%`;

  if (runway.runwayValue === FINANCIAL_CONSTANTS.FULLY_COVERED) {
    return {
      hasRunwayFromProfile: true,
      runwayIncreaseLabel: 'Fully Covered',
      runwayIsFullyCovered: true,
      longevityIncreaseLabel,
    };
  }

  const parsed = parseFloat(runway.runwayValue);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { hasRunwayFromProfile: false, runwayIncreaseLabel: null, longevityIncreaseLabel };
  }

  const runwayValues = buildRunwaySeries(parsed, 4);
  const baseIncrease = Math.max(0, (runwayValues[runwayValues.length - 1] ?? parsed) - (runwayValues[0] ?? parsed));
  const increaseMonths = Math.round(baseIncrease * completionRatio * 10) / 10;

  return {
    hasRunwayFromProfile: true,
    runwayIncreaseLabel: formatRunwayIncreaseLabel(increaseMonths) ?? '+0 months',
    runwayIsFullyCovered: false,
    longevityIncreaseLabel,
  };
}

/**
 * Shared runway, longevity, and wellness metrics for Quick Richuel rewards.
 */
export function computeQuickRichuelImpactMetrics({
  completedCount = 0,
  totalCount = 0,
  checkInHistory = [],
  v2OnboardingAiResult = null,
} = {}) {
  const richuelDates = getRichuelCompletedDateSet(checkInHistory);
  const anxietyInsight = computeAnxietyDropInsight(checkInHistory, richuelDates, 2);
  const confidenceInsight = computeConfidenceRiseInsight(checkInHistory, richuelDates, 2);
  const financialImpact = resolveQuickRichuelFinancialImpact(
    v2OnboardingAiResult,
    completedCount,
    totalCount,
  );

  return {
    completedCount,
    totalCount,
    completionRatio: totalCount > 0 ? completedCount / totalCount : 0,
    confidenceRisePct: confidenceInsight.confidenceRisePct,
    anxietyDropPct: anxietyInsight.anxietyDropPct,
    hasWellnessData: confidenceInsight.hasRealData || anxietyInsight.hasRealData,
    hasFinancialData: Boolean(financialImpact.hasRunwayFromProfile),
    financialImpact,
  };
}

function rewardTiers(tiers) {
  return (Array.isArray(tiers) ? tiers : []).map((tier) => ({
    id: tier.id,
    title: tier.title,
    minWeekRichuelDays: tier.min_week_richuel_days,
    minStreak: tier.min_streak,
  }));
}

function rewardTitle(tiers, id) {
  return tiers.find((tier) => tier.id === id)?.title || '';
}

function fillReward(template, values) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (_, key) => (
    values[key] == null ? '' : String(values[key])
  ));
}

function daySuffix(count) {
  return count === 1 ? '' : 's';
}

function formatQuickRichuelTaskProgress(completedCount, totalCount) {
  if (completedCount <= 0) return null;
  if (totalCount > 0) {
    return `${completedCount}/${totalCount} Quick Richuel tasks`;
  }
  const word = completedCount === 1 ? 'task' : 'tasks';
  return `${completedCount} Quick Richuel ${word}`;
}

function buildWeeklyRewardHighlights(metrics, weeklyInsight) {
  const highlights = [];
  const financial = metrics?.financialImpact ?? {};
  const confidenceRise = metrics?.confidenceRisePct ?? 0;
  const anxietyDrop = metrics?.anxietyDropPct ?? 0;
  const weeklyConfidenceChange = weeklyInsight?.confidenceChange ?? 0;

  if (financial.hasRunwayFromProfile && metrics?.completedCount > 0) {
    if (financial.runwayIsFullyCovered) {
      highlights.push('Runway Fully Covered');
    } else if (financial.runwayIncreaseLabel) {
      highlights.push(`Runway ${financial.runwayIncreaseLabel}`);
    }
    if (financial.longevityIncreaseLabel) {
      highlights.push(`Longevity ${financial.longevityIncreaseLabel}`);
    }
  }

  if (confidenceRise > 0) {
    highlights.push(`Confidence up ${confidenceRise}%`);
  } else if (weeklyConfidenceChange > 0) {
    highlights.push(`Confidence up ${weeklyConfidenceChange} pt${weeklyConfidenceChange === 1 ? '' : 's'} this week`);
  }

  if (anxietyDrop > 0) {
    highlights.push(`Anxiety down ${anxietyDrop}%`);
  }

  return highlights;
}

function resolveWeeklyRewardTier({
  tiers,
  weekRichuelCount,
  streak,
  completedTasks,
  metrics,
}) {
  const byId = Object.fromEntries(tiers.map((tier) => [tier.id, tier]));
  const confidenceRise = metrics?.confidenceRisePct ?? 0;
  const anxietyDrop = metrics?.anxietyDropPct ?? 0;
  const hasWellnessWin = confidenceRise > 0 || anxietyDrop > 0;
  const pro = byId.pro;
  const hero = byId.hero;
  const calm = byId.calm;
  const climber = byId.climber;
  const builder = byId.builder;

  if (pro && (weekRichuelCount >= pro.minWeekRichuelDays || streak >= pro.minStreak)) return pro;
  if (hero && (
    weekRichuelCount >= hero.minWeekRichuelDays
    || streak >= hero.minStreak
    || (completedTasks >= 4 && weekRichuelCount >= 3)
  )) {
    return hero;
  }
  if (calm && (weekRichuelCount >= calm.minWeekRichuelDays || streak >= calm.minStreak)) return calm;
  if (climber && (weekRichuelCount >= climber.minWeekRichuelDays || completedTasks >= 2 || hasWellnessWin)) {
    return climber;
  }
  if (builder && (weekRichuelCount >= builder.minWeekRichuelDays || completedTasks >= 1 || streak >= builder.minStreak)) {
    return builder;
  }
  return null;
}

function buildWeeklyRewardNextMilestone(tier, tiers, copy, weekRichuelCount, streak, completedTasks) {
  const names = {
    pro: rewardTitle(tiers, 'pro'),
    hero: rewardTitle(tiers, 'hero'),
    calm: rewardTitle(tiers, 'calm'),
    climber: rewardTitle(tiers, 'climber'),
    builder: rewardTitle(tiers, 'builder'),
  };
  const daysLine = (count, tierName) => fillReward(copy.more_days, {
    count,
    suffix: daySuffix(count),
    tier: tierName,
  });
  const streakLine = (count, tierName) => fillReward(copy.more_streak, {
    count,
    suffix: daySuffix(count),
    tier: tierName,
  });

  if (!tier) {
    return fillReward(copy.empty_next_milestone, names);
  }

  if (tier.id === 'pro') return copy.pro_unlocked;

  if (tier.id === 'hero') {
    const daysNeeded = Math.max(0, (tiers.find((item) => item.id === 'pro')?.minWeekRichuelDays || 0) - weekRichuelCount);
    if (daysNeeded > 0) return daysLine(daysNeeded, names.pro);
    const proStreak = tiers.find((item) => item.id === 'pro')?.minStreak || 0;
    if (streak < proStreak) return streakLine(proStreak - streak, names.pro);
    return fillReward(copy.maintain_pro, names);
  }

  if (tier.id === 'calm') {
    const heroDays = tiers.find((item) => item.id === 'hero')?.minWeekRichuelDays || 0;
    const daysNeeded = Math.max(0, heroDays - weekRichuelCount);
    if (daysNeeded > 0) return daysLine(daysNeeded, names.hero);
    const heroStreak = tiers.find((item) => item.id === 'hero')?.minStreak || 0;
    const streakNeeded = Math.max(0, heroStreak - streak);
    if (streakNeeded > 0) return streakLine(streakNeeded, names.hero);
    return fillReward(copy.more_tasks_hero, names);
  }

  if (tier.id === 'climber') {
    const calmDays = tiers.find((item) => item.id === 'calm')?.minWeekRichuelDays || 0;
    const daysNeeded = Math.max(0, calmDays - weekRichuelCount);
    if (daysNeeded > 0) return daysLine(daysNeeded, names.calm);
    return fillReward(copy.streak_calm, names);
  }

  if (tier.id === 'builder') {
    if (completedTasks < 2) return copy.another_task;
    const climberDays = tiers.find((item) => item.id === 'climber')?.minWeekRichuelDays || 0;
    const daysNeeded = Math.max(0, climberDays - weekRichuelCount);
    return daysLine(daysNeeded, names.climber);
  }

  return null;
}

/**
 * Builds tiered weekly reward copy aligned with Quick Richuel impact metrics.
 */
export function buildWeeklyRichuelReward({
  streak = 0,
  weekRichuelCount = 0,
  metrics = null,
  weeklyInsight = null,
  rewards = null,
} = {}) {
  const tiers = rewardTiers(rewards?.tiers);
  const copy = rewards?.copy || {};
  const completedTasks = metrics?.completedCount ?? 0;
  const totalTasks = metrics?.totalCount ?? 0;
  const tier = resolveWeeklyRewardTier({
    tiers,
    weekRichuelCount,
    streak,
    completedTasks,
    metrics,
  });

  if (!tier) {
    return {
      label: copy.next_label || '',
      title: rewardTitle(tiers, 'builder'),
      subtitle: copy.empty_subtitle || '',
      detail: metrics?.hasFinancialData ? copy.detail_ready : copy.detail_needs_profile,
      nextMilestone: buildWeeklyRewardNextMilestone(null, tiers, copy, weekRichuelCount, streak, completedTasks),
      tier: null,
      isEarned: false,
    };
  }

  const achievementParts = [];
  if (weekRichuelCount > 0) achievementParts.push(`${weekRichuelCount}/7 richuel days`);
  const taskProgress = formatQuickRichuelTaskProgress(completedTasks, totalTasks);
  if (taskProgress) achievementParts.push(taskProgress);
  if (streak >= 2) achievementParts.push(`${streak}-day streak`);

  const highlights = buildWeeklyRewardHighlights(metrics, weeklyInsight);
  const subtitle = achievementParts.length
    ? achievementParts.join(' · ')
    : (copy.showed_up || '');

  let detail = highlights.join(' · ');
  if (!detail && metrics?.hasFinancialData && completedTasks > 0) {
    detail = copy.compounding;
  } else if (!detail && weekRichuelCount > 0) {
    detail = copy.reflect;
  }

  return {
    label: copy.earned_label || '',
    title: tier.title,
    subtitle,
    detail: detail || null,
    nextMilestone: buildWeeklyRewardNextMilestone(tier, tiers, copy, weekRichuelCount, streak, completedTasks),
    tier: tier.id,
    isEarned: true,
  };
}

function average(values) {
  const valid = values.filter((value) => value != null && Number.isFinite(value));
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function percentDrop(start, end) {
  if (start == null || end == null || start <= 0 || end >= start) return 0;
  return Math.round(((start - end) / start) * 100);
}

function averageMovement(days) {
  if (days.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < days.length; i += 1) {
    total += Math.abs(days[i].confidence - days[i - 1].confidence);
    total += Math.abs(days[i].anxiety - days[i - 1].anxiety);
  }
  return total / ((days.length - 1) * 2);
}

function buildWeeklyInsight({
  chartDays,
  weekRichuelCount,
  richuelThreshold,
  anxietyDropPct,
  hasRealData,
}) {
  const firstHalf = chartDays.slice(0, 3);
  const lastHalf = chartDays.slice(-3);
  const startAnxiety = average(firstHalf.map((day) => day.anxiety));
  const endAnxiety = average(lastHalf.map((day) => day.anxiety));
  const startConfidence = average(firstHalf.map((day) => day.confidence));
  const endConfidence = average(lastHalf.map((day) => day.confidence));
  const weeklyAnxietyDropPct = percentDrop(startAnxiety, endAnxiety);
  const confidenceChange = startConfidence == null || endConfidence == null
    ? 0
    : Math.round(endConfidence - startConfidence);
  const startMovement = averageMovement(firstHalf);
  const endMovement = averageMovement(lastHalf);
  const volatilityDropPct = percentDrop(startMovement, endMovement);
  const richuelWord = weekRichuelCount === 1 ? 'richuel' : 'richuels';
  const displayDropPct = hasRealData
    ? anxietyDropPct
    : weeklyAnxietyDropPct;

  if (weekRichuelCount >= richuelThreshold && displayDropPct >= 10) {
    return {
      title: 'Good News!',
      sentence: `When you completed ${weekRichuelCount} ${richuelWord}, your stress went down by ${displayDropPct}%.`,
      detail: 'Your richuel days are marked on the graph, so you can see how routine connects to calmer reflections.',
      sourceNote: hasRealData
        ? 'Based on your recent Quick Richuel reflections and richuel data.'
        : 'Based on your 7-day trend so far.',
      moodTag: 'Stress is easing',
      anxietyDropPct: displayDropPct,
      confidenceChange,
      volatilityDropPct,
    };
  }

  if (weekRichuelCount >= richuelThreshold && confidenceChange > 0) {
    return {
      title: 'Good Momentum!',
      sentence: `You completed ${weekRichuelCount} ${richuelWord}, and your confidence finished ${confidenceChange} point${confidenceChange === 1 ? '' : 's'} higher.`,
      detail: 'Keep pairing finished Quick Richuels with how you feel so Richuel can learn which routines lower your anxiety most.',
      sourceNote: 'Based on your 7-day confidence trend.',
      moodTag: 'Confidence is rising',
      anxietyDropPct: displayDropPct,
      confidenceChange,
      volatilityDropPct,
    };
  }

  if (weekRichuelCount > 0 && volatilityDropPct >= 10) {
    return {
      title: 'Your Routine Is Settling In',
      sentence: `After ${weekRichuelCount} ${richuelWord}, your mood swings became ${volatilityDropPct}% steadier by the end of the week.`,
      detail: 'The goal is not a perfect line. It is seeing the bumps get smaller as your routine repeats.',
      sourceNote: 'Based on the movement between your confidence and anxiety scores.',
      moodTag: 'Emotions are smoothing',
      anxietyDropPct: displayDropPct,
      confidenceChange,
      volatilityDropPct,
    };
  }

  if (weekRichuelCount > 0) {
    return {
      title: 'You Started The Pattern',
      sentence: `You completed ${weekRichuelCount} ${richuelWord} this week. Reach ${richuelThreshold} to unlock a clearer stress trend.`,
      detail: 'A few more richuel days will make the connection between actions and emotions easier to see.',
      sourceNote: 'Reflect after more finished Quick Richuels to improve this insight.',
      moodTag: 'More data needed',
      anxietyDropPct: displayDropPct,
      confidenceChange,
      volatilityDropPct,
    };
  }

  return {
    title: 'Start With One Small Richuel',
    sentence: `Complete ${richuelThreshold} richuels this week to see how your routine affects stress and confidence.`,
    detail: 'This report gets stronger when richuel completions and Quick Richuel reflections appear together.',
    sourceNote: 'No richuel completions were found for this week yet.',
    moodTag: 'Ready to build',
    anxietyDropPct: displayDropPct,
    confidenceChange,
    volatilityDropPct,
  };
}

/**
 * Builds chart series, insight copy, and streak data for the Your Weekly Wins UI.
 */
export function buildYourWeeklyWinsData({
  checkInHistory = [],
  referenceDate = new Date(),
} = {}) {
  const weekDates = getWeekDateStringsMonFirst(referenceDate);
  const richuelDates = getRichuelCompletedDateSet(checkInHistory);
  const byDate = averageReflectionsByDate(checkInHistory);

  const rawConfidence = weekDates.map((date) => {
    const entry = byDate[date];
    return entry?.confidence != null ? scaleMetricToChart(entry.confidence) : null;
  });
  const rawAnxiety = weekDates.map((date) => {
    const entry = byDate[date];
    return entry?.anxiety != null ? scaleMetricToChart(entry.anxiety) : null;
  });

  const confidence = interpolateMetricValues(rawConfidence);
  const anxiety = interpolateMetricValues(rawAnxiety);

  const chartDays = WEEK_DAY_LABELS.map((label, i) => ({
    label,
    date: weekDates[i],
    confidence: confidence[i],
    anxiety: anxiety[i],
    richuelCompleted: richuelDates.has(weekDates[i]),
    hasReflection: rawConfidence[i] != null || rawAnxiety[i] != null,
  }));

  const weekRichuelCount = weekDates.filter((d) => richuelDates.has(d)).length;
  const baseInsight = computeAnxietyDropInsight(checkInHistory, richuelDates);
  const insight = {
    ...baseInsight,
    ...buildWeeklyInsight({
      chartDays,
      weekRichuelCount,
      richuelThreshold: baseInsight.richuelThreshold,
      anxietyDropPct: baseInsight.anxietyDropPct,
      hasRealData: baseInsight.hasRealData,
    }),
  };
  const streak = calculateStreak(checkInHistory);

  const hasVolatileStart = chartDays.slice(0, 3).some((d, i, arr) => {
    if (i === 0) return false;
    return Math.abs(d.confidence - arr[i - 1].confidence) >= 2
      || Math.abs(d.anxiety - arr[i - 1].anxiety) >= 2;
  });
  const hasCalmEnd = chartDays.slice(-3).every((d) => d.anxiety <= 4 && d.confidence >= 6);

  return {
    weekRangeLabel: formatWeekRangeLabel(weekDates),
    chartDays,
    insight,
    streak,
    weekRichuelCount,
    closingMessage: hasCalmEnd || streak >= 3
      ? 'Your routine is working. Keep it up!'
      : 'Every finished Quick Richuel builds clarity. Keep showing up!',
    showRichuelInsight: weekRichuelCount > 0 || hasVolatileStart,
    weekReflectionCount: countReflectionsOnDates(checkInHistory, weekDates),
  };
}

const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dateInRange(dateStr, startDate, endDate) {
  return dateStr >= startDate && dateStr <= endDate;
}

function getLastNMonthBuckets(n, referenceDate = new Date()) {
  const buckets = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    buckets.push({
      year,
      month,
      label: MONTH_LABELS_SHORT[month],
      startDate: getLocalDateString(new Date(year, month, 1)),
      endDate: getLocalDateString(new Date(year, month + 1, 0)),
    });
  }
  return buckets;
}

function countRichuelsInRange(richuelDates, startDate, endDate) {
  let count = 0;
  richuelDates.forEach((date) => {
    if (dateInRange(date, startDate, endDate)) count += 1;
  });
  return count;
}

function averageConfidenceInRange(history, startDate, endDate) {
  const values = normalizeCheckInHistory(history)
    .filter((entry) => dateInRange(entry?.date, startDate, endDate) && entry?.confidence != null)
    .map((entry) => Number(entry.confidence))
    .filter((value) => Number.isFinite(value));
  return average(values);
}

function formatRunwayMonths(value) {
  if (value == null || !Number.isFinite(value)) return null;
  const rounded = Math.round(value * 10) / 10;
  const display = rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
  return `${display} ${rounded === 1 ? 'month' : 'months'}`;
}

function resolveRunwayMonths(runwayMonths) {
  if (runwayMonths != null && Number.isFinite(runwayMonths) && runwayMonths > 0) {
    return Math.round(runwayMonths * 10) / 10;
  }
  return null;
}

function resolveRunwayLabel(runwayMonths, runwayDisplayLabel) {
  if (typeof runwayDisplayLabel === 'string' && runwayDisplayLabel.trim()) {
    return runwayDisplayLabel.trim();
  }
  return formatRunwayMonths(runwayMonths);
}

function buildRunwaySeries(endRunway, monthCount = 4) {
  if (endRunway == null) {
    return Array.from({ length: monthCount }, () => null);
  }
  const end = endRunway;
  const start = Math.max(1, Math.round((end - Math.max(0.75, end * 0.25)) * 10) / 10);
  if (monthCount <= 1) return [end];
  return Array.from({ length: monthCount }, (_, i) => {
    const t = i / (monthCount - 1);
    return Math.round((start + (end - start) * t) * 10) / 10;
  });
}

function formatMonthWinsLabel(referenceDate = new Date()) {
  return referenceDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function countReflectionsInRange(history, startDate, endDate) {
  return normalizeCheckInHistory(history).filter((entry) => (
    entry?.date
    && dateInRange(entry.date, startDate, endDate)
    && (entry.confidence != null || entry.anxiety != null)
  )).length;
}

function formatSavingsIncreaseLabel(pct) {
  if (pct == null) return '—';
  if (pct > 0) return `+${pct}%`;
  if (pct < 0) return `${pct}%`;
  return 'No change';
}

function buildMonthlySummaryText({
  startRunwayLabel,
  endRunwayLabel,
  savingsIncreasePct,
  confidenceScore,
  hasRunwayFromProfile,
  hasConfidenceData,
  monthCheckInCount,
  monthRichuelCount,
}) {
  const richuelWord = monthRichuelCount === 1 ? 'richuel day' : 'richuel days';
  const reflectionWord = monthCheckInCount === 1 ? 'reflection' : 'reflections';

  if (hasRunwayFromProfile && savingsIncreasePct > 0 && hasConfidenceData) {
    return `As your runway expanded from ${startRunwayLabel} to ${endRunwayLabel}, your financial confidence reached ${confidenceScore}/10 this month. Your safety net is growing alongside your richuels.`;
  }

  if (hasRunwayFromProfile && savingsIncreasePct === 0) {
    const confidencePart = hasConfidenceData
      ? `Your confidence score is ${confidenceScore}/10.`
      : 'Reflect after you finish a Quick Richuel to track how your mood shifts over time.';
    return `Your runway holds at ${endRunwayLabel}. ${confidencePart} Staying consistent keeps your plan steady.`;
  }

  if (hasRunwayFromProfile) {
    return `Your runway is ${endRunwayLabel}. ${hasConfidenceData ? `Confidence this month: ${confidenceScore}/10.` : 'Reflect after more finished Quick Richuels to connect richuels with how you feel.'}`;
  }

  if (monthRichuelCount > 0 || monthCheckInCount > 0) {
    const parts = [];
    if (monthRichuelCount > 0) parts.push(`${monthRichuelCount} ${richuelWord}`);
    if (monthCheckInCount > 0) parts.push(`${monthCheckInCount} ${reflectionWord}`);
    const activity = parts.join(' and ');
    const confidencePart = hasConfidenceData
      ? ` Confidence this month: ${confidenceScore}/10.`
      : ' Complete your financial profile to unlock runway trends.';
    return `You logged ${activity} this month.${confidencePart}`;
  }

  return 'Complete your financial profile and daily richuels to unlock personalized runway and confidence trends next month.';
}

export function getWeeklyStressTrendLabel({ anxietyDropPct = 0, chartDays = [] } = {}) {
  if (!chartDays.length) return 'Steady';
  const firstHalfAnxiety = chartDays.slice(0, 3).reduce((s, d) => s + d.anxiety, 0) / 3;
  const lastHalfAnxiety = chartDays.slice(-3).reduce((s, d) => s + d.anxiety, 0) / 3;
  const anxietyDiff = firstHalfAnxiety - lastHalfAnxiety;
  if (anxietyDropPct > 0) return `${anxietyDropPct}% lower`;
  if (anxietyDiff > 0.5) return 'Easing';
  if (anxietyDiff < -0.5) return 'Rising';
  return 'Steady';
}

export function getConfidenceChangeLabel(confidenceChange = 0) {
  if (confidenceChange > 0) return `Up ${confidenceChange} pts`;
  if (confidenceChange < 0) return `Down ${Math.abs(confidenceChange)} pts`;
  return 'Steady';
}

/**
 * Builds chart series, summary copy, and stats for the Your Monthly Wins UI.
 */
export function buildYourMonthlyWinsData({
  checkInHistory = [],
  runwayMonths = null,
  runwayDisplayLabel = null,
  referenceDate = new Date(),
} = {}) {
  const monthBuckets = getLastNMonthBuckets(4, referenceDate);
  const richuelDates = getRichuelCompletedDateSet(checkInHistory);

  const currentBucket = monthBuckets[monthBuckets.length - 1];
  const monthRichuelCount = countRichuelsInRange(
    richuelDates,
    currentBucket.startDate,
    currentBucket.endDate,
  );

  const previousBucket = monthBuckets[monthBuckets.length - 2];
  const previousMonthRichuelCount = previousBucket
    ? countRichuelsInRange(richuelDates, previousBucket.startDate, previousBucket.endDate)
    : 0;

  const hasRunwayFromProfile = runwayMonths != null
    && Number.isFinite(runwayMonths)
    && runwayMonths > 0;
  const endRunway = resolveRunwayMonths(runwayMonths);
  const runwayValues = buildRunwaySeries(endRunway, monthBuckets.length);
  const startRunway = runwayValues[0];
  const startRunwayLabel = startRunway != null
    ? formatRunwayMonths(startRunway)
    : null;
  const endRunwayLabel = endRunway != null
    ? resolveRunwayLabel(endRunway, runwayDisplayLabel)
    : null;

  const rawConfidence = monthBuckets.map((bucket) => (
    averageConfidenceInRange(checkInHistory, bucket.startDate, bucket.endDate)
  ));
  const confidenceValues = interpolateMetricValues(
    rawConfidence.map((value) => (
      value != null ? scaleMetricToChart(value) : null
    )),
  );

  const chartMonths = monthBuckets.map((bucket, i) => ({
    label: bucket.label,
    runway: runwayValues[i],
    confidence: confidenceValues[i],
    hasConfidenceData: rawConfidence[i] != null,
  }));

  const currentConfidenceRaw = averageConfidenceInRange(
    checkInHistory,
    currentBucket.startDate,
    currentBucket.endDate,
  );
  const hasConfidenceData = rawConfidence.some((value) => value != null);
  const confidenceScore = currentConfidenceRaw != null
    ? formatCheckInScore(currentConfidenceRaw)
    : hasConfidenceData
      ? formatCheckInScore(confidenceValues[confidenceValues.length - 1])
      : null;

  const savingsIncreasePct = hasRunwayFromProfile && startRunway > 0 && endRunway != null
    ? Math.max(0, Math.round(((endRunway - startRunway) / startRunway) * 100))
    : null;
  const monthReflectionCount = countReflectionsInRange(
    checkInHistory,
    currentBucket.startDate,
    currentBucket.endDate,
  );

  const isBestMonth = monthRichuelCount >= previousMonthRichuelCount;
  const heroTitle = monthRichuelCount > 0
    ? `Congrats! You finished ${monthRichuelCount} ${monthRichuelCount === 1 ? 'richuel' : 'richuels'} this month!`
    : 'Your monthly wins are here';
  const heroSubtitle = monthRichuelCount === 0
    ? 'Start or resume your daily richuels to build momentum. Small steps add up to big financial confidence.'
    : isBestMonth
      ? 'Your dedication is paying off. This is your best month yet, showing consistent growth across all your financial and personal metrics.'
      : 'Your dedication is paying off. Keep building your routine to unlock even stronger growth next month.';
  const closingMessage = monthRichuelCount > 0
    ? (isBestMonth
      ? 'Your routine is working. Keep it up!'
      : 'Want to grow your score next month? Keep doing your Daily Richuels!')
    : 'Every finished Quick Richuel builds clarity. Keep showing up!';

  return {
    monthLabel: formatMonthWinsLabel(referenceDate),
    monthRichuelCount,
    heroTitle,
    heroSubtitle,
    closingMessage,
    chartMonths,
    hasRunwayFromProfile,
    hasConfidenceData,
    monthReflectionCount,
    summaryText: buildMonthlySummaryText({
      startRunwayLabel,
      endRunwayLabel,
      savingsIncreasePct,
      confidenceScore,
      hasRunwayFromProfile,
      hasConfidenceData,
      monthCheckInCount: monthReflectionCount,
      monthRichuelCount,
    }),
    summary: {
      startRunway,
      endRunway,
      startRunwayLabel,
      endRunwayLabel,
    },
    stats: {
      richuelsKept: monthRichuelCount,
      savingsIncreasePct,
      savingsIncreaseLabel: formatSavingsIncreaseLabel(savingsIncreasePct),
      confidenceScore,
      monthReflectionCount,
    },
  };
}
